import { useEffect } from 'react';

/**
 * Lightweight per-page SEO component. Mount inside any page to set the
 * <title>, meta description, canonical URL and OG/Twitter title+description
 * for that page. Falls back to the site defaults in public/index.html when
 * a page does not render <SEO />.
 *
 * Usage:
 *   <SEO
 *     title="About Dr Joseph Cheriyan · Cheriyan Group"
 *     description="Consultant Clinical Pharmacologist at Cambridge University Hospitals..."
 *     path="/about"
 *   />
 */
export default function SEO({ title, description, path }) {
  useEffect(() => {
    const prevTitle = document.title;

    if (title) document.title = title;

    const setMeta = (selector, attr, value) => {
      if (value == null) return null;
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement('meta');
        const [key, val] = selector.replace(/[[\]"]/g, '').split('=');
        el.setAttribute(key, val);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return { el, prev, created };
    };

    const setLink = (rel, href) => {
      if (!href) return null;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute('href');
      el.setAttribute('href', href);
      return { el, prev, created };
    };

    const restores = [];

    if (description) {
      restores.push(['meta', setMeta('meta[name="description"]', 'content', description)]);
      restores.push(['meta', setMeta('meta[property="og:description"]', 'content', description)]);
      restores.push(['meta', setMeta('meta[name="twitter:description"]', 'content', description)]);
    }
    if (title) {
      restores.push(['meta', setMeta('meta[property="og:title"]', 'content', title)]);
      restores.push(['meta', setMeta('meta[name="twitter:title"]', 'content', title)]);
    }
    if (path) {
      const canonical = `https://cheriyangroup.org${path}`;
      restores.push(['link', setLink('canonical', canonical)]);
      restores.push(['meta', setMeta('meta[property="og:url"]', 'content', canonical)]);
    }

    return () => {
      document.title = prevTitle;
      for (const [, r] of restores) {
        if (!r) continue;
        if (r.created) {
          r.el.remove();
        } else if (r.prev != null) {
          const attr = r.el.tagName === 'LINK' ? 'href' : 'content';
          r.el.setAttribute(attr, r.prev);
        }
      }
    };
  }, [title, description, path]);

  return null;
}
